/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, getTelemetryObject } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IRefineMenuViewProps,
    ISearchResultContainerData,
    ISearchResultContainerResources,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';

import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import { addPickAndMixListAsInfocodes_CAPAsync } from '../../../actions/DataActionExtension.g';
import { ISearchResultContainerProps as ISearchResultContainerExtensionProps } from '../definition-extensions/search-result-container.ext.props.autogenerated';

import { ICartViewProps } from '@msdyn365-commerce-modules/cart';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { CartLine, CommerceProperty, ProductRefinerValue, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import ProductComponent from './components/product.component';

import { getCategoryPageString, getSearchPageString } from '../utilities/datalayer/helpers';
import { trackAddToCartFromListing, trackAddVenchiMix, trackClickFilter, trackViewItemList } from '../utilities/datalayer/index';

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps &
    ICartViewProps &
    ISearchResultContainerExtensionProps<ISearchResultContainerData>> = props => {
    const {
        SearchResultContainer,
        products,
        pagination,
        ProductsContainer,
        ProductSectionContainer,
        choiceSummary,
        // isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        sortByOptions,
        RefineAndProductSectionContainer,
        errorMessage,
        FeatureSearchContainer,
        similarLookProduct,
        config,
        data
    } = props;

    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    const catPage =
        props.context &&
        props.context.request &&
        props.context.request.urlTokens &&
        props.context.request.urlTokens.categories &&
        props.context.request.urlTokens.categories[0];
    const { categoryH1Title, isSearchPage, displayTitle, hideTitle } = config;

    const refinerStatusCount =
        props.data.listPageState.result && props.data.listPageState.result.activeFilters?.length
            ? props.data.listPageState.result.activeFilters?.length
            : null;

    const categoryNamesArray = data.categoryHierarchy.result
        ?.map(category => category.Name)
        .filter(name => name !== undefined)
        .concat(Array(5).fill(''))
        .slice(0, 5) as string[];
    const categoryUserPathString = categoryNamesArray.join('|');
    const listName = props.config.isSearchPage
        ? getSearchPageString()
        : data.categoryHierarchy.result?.[data.categoryHierarchy.result?.length - 1]?.Name ?? getCategoryPageString();

    React.useEffect(() => {
        const products = (data.listPageState.result && data.listPageState.result.activeProducts) || [];

        trackViewItemList(props.context, products.filter(p => p) as SimpleProduct[], listName, categoryUserPathString);
    }, []);

    React.useEffect(() => {
        const setupEllipsis = () => {
            const descriptionElements = document.querySelectorAll('.category-description-text p');
            descriptionElements.forEach(element => {
                const htmlElement = element as HTMLElement;
                const originalHeight = htmlElement.scrollHeight;
                const visibleHeight = htmlElement.clientHeight;
                const buttonContainer = document.createElement('div');
                buttonContainer.className = 'button-container';
                const toggleButton = document.createElement('button');
                toggleButton.className = 'msc-cta____secondary';
                toggleButton.textContent = props.resources?.readMoreText;
                toggleButton.addEventListener('click', () => {
                    htmlElement.classList.toggle('expanded');
                    if (toggleButton.textContent === props.resources?.readMoreText) {
                        toggleButton.textContent = props.resources?.readLessText;
                    } else {
                        toggleButton.textContent = props.resources?.readMoreText;
                    }
                });
                buttonContainer.appendChild(toggleButton);
                if (htmlElement.parentElement) {
                    htmlElement.parentElement.insertBefore(buttonContainer, htmlElement.nextSibling);
                }

                if (originalHeight <= visibleHeight + 10) {
                    htmlElement.classList.add('no-ellipsis');
                    buttonContainer.classList.add('hidden');
                }
            });
        };

        const timer = setTimeout(setupEllipsis, 100);

        return () => clearTimeout(timer);
    }, []);

    const currentFilterCount = props.data.listPageState.result?.activeFilters?.length || 0;

    const [isFilterSeen, setIsFilterSeen] = React.useState<boolean>(false);
    const [prevFilterCount, setPrevFilterCount] = React.useState<number>(currentFilterCount);
    const [prevFilters, setPrevFilters] = React.useState<ProductRefinerValue[]>(props.data.listPageState.result?.activeFilters || []);

    React.useEffect(() => {
        if (isFilterSeen) {
            if (props.data.listPageState.result?.activeFilters?.length && props.data.refiners.result) {
                const currentFilterCount = props.data.listPageState.result?.activeFilters?.length;

                if (currentFilterCount > prevFilterCount) {
                    const set1 = new Set(props.data.listPageState.result?.activeFilters.map(a => JSON.stringify(a)));
                    const set2 = new Set(prevFilters.map(a => JSON.stringify(a)));

                    const difference = new Set([...set1].filter(x => !set2.has(x)));

                    if (difference.size === 1) {
                        const uniqueString = difference.values().next().value;
                        const currentFilter = props.data.listPageState.result?.activeFilters.find(a => JSON.stringify(a) === uniqueString);

                        if (currentFilter?.LeftValueBoundString) {
                            for (let item of props.data.refiners.result) {
                                if (item.RecordId === currentFilter?.RefinerRecordId) {
                                    if (item.RecordId === 0) {
                                        if (item.Values[0].UnitText) {
                                            trackClickFilter(item.KeyName, '', {
                                                min: Number(currentFilter?.LeftValueBoundString),
                                                max: Number(currentFilter?.RightValueBoundString)
                                            });
                                        }
                                    } else {
                                        trackClickFilter(item.KeyName, currentFilter.LeftValueBoundString);
                                    }
                                }
                            }
                        }
                    }
                } else if (currentFilterCount === prevFilterCount) {
                    // Price filter change
                    const currentFilter = props.data.listPageState.result?.activeFilters.find(a => a.UnitText);

                    for (let item of props.data.refiners.result) {
                        if (item.RecordId === currentFilter?.RefinerRecordId) {
                            if (item.RecordId === 0) {
                                if (item.Values[0].UnitText) {
                                    trackClickFilter(item.KeyName, '', {
                                        min: Number(currentFilter?.LeftValueBoundString),
                                        max: Number(currentFilter?.RightValueBoundString)
                                    });
                                }
                            }
                        }
                    }
                }

                setPrevFilterCount(currentFilterCount);
                setPrevFilters(props.data.listPageState.result?.activeFilters);
            }
        } else {
            setIsFilterSeen(true);
        }
    }, [props.data.listPageState.result?.activeFilters?.length]);

    /**
     * Get Active products.
     * @returns - JSX Elements.
     */
    const _getProducts = (): JSX.Element => {
        const {
            config: { imageSettings, allowBackNavigation: shouldAllowBackNavigation }
        } = props;
        const { resources } = props;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Reading config value.
        const breadCrumbType: string = props.context.app.config.breadcrumbType;
        const shouldAllowBack = shouldAllowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');

        const products = (data.listPageState.result && data.listPageState.result.activeProducts) || [];
        const quickviewSlot = props.slots?.quickview?.[0];
        const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
            ? props.slots.productComparisonButton[0]
            : null;

        const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        return (
            <ProductSearchResultItems
                products={products}
                context={props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={props.typeName}
                moduleId={props.id}
                allowBackNavigation={shouldAllowBack}
                telemetryContent={getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry)}
                quickviewSlot={quickviewSlot}
                productComparisonButton={productComparisonButton}
                channelInventoryConfigurationId={props.data.products.result?.channelInventoryConfigurationId}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                //DISABLED FOR NOW
                productsDimensionAvailabilities={undefined}
                //DATALAYER INFO
                productCategoryUserPath={isSearchPage ? undefined : categoryUserPathString}
                productListName={listName}
            />
        );
    };

    if (catPage === 'pick-mix') {
        React.useEffect(() => {
            const introCtn = document.getElementsByClassName('category-intro__ctn')?.[0];
            const intro = document.getElementsByClassName('category-intro')?.[0];
            if (introCtn && intro) {
                introCtn?.appendChild(intro);
            }
        }, []);
        const localPm = typeof window !== 'undefined' && localStorage.getItem('pm_products');
        const localPmProds = localPm && localPm !== null ? localPm : JSON.stringify({});

        const pmProductsList = JSON.parse(localPmProds);
        //const cookiePmProducts = cookies.get('pm_products') === undefined ? {} : cookies.get('pm_products');
        // @ts-ignore
        props.context.pmProducts = pmProductsList;
        const [pmProducts, setPmProducts] = React.useState(pmProductsList);
        // @ts-ignore
        const initialTotalItems: number = Object.values(pmProducts).reduce((t, { Qty }) => Number(t) + Number(Qty), 0);
        // @ts-ignore
        const totalProducts = Object.values(pmProducts).map(tp => tp.Qty * tp.Price);
        const initialTotalPrice: number = Number(totalProducts.reduce((tp, qp) => tp + qp, 0));
        const [totalPrice, setTotalPrice] = React.useState(initialTotalPrice);
        const [totalItems, setTotalItems] = React.useState<number>(initialTotalItems);
        const [pmCartMessage, setPmCartMessage] = React.useState({
            status: 'empty',
            message: props.resources.productSelectionSpaceText
        });
        const [openPmCart, setOpenPmCart] = React.useState(false);

        const cookiePMset = () => {
            typeof window !== 'undefined' && localStorage.setItem('pm_products', JSON.stringify(pmProducts));
            const localPm = localStorage && localStorage.getItem('pm_products');
            const localPmProds = localPm && localPm !== null ? localPm : JSON.stringify({});
            const pmProductsList = JSON.parse(localPmProds);
            setPmProducts(pmProductsList);
        };

        const updatePmCart = () => {
            setPmProducts(pmProductsList);
        };

        React.useEffect(() => {
            const wrapper = document.getElementsByClassName('ms-search-result-container__Products')[0];
            // @ts-ignore
            const handleButtonClick = event => {
                const isButton = event.target.nodeName === 'BUTTON';
                if (!isButton) {
                    return;
                }
                const timer = setTimeout(() => {
                    updatePmCart();
                }, 100);
                return () => {
                    clearTimeout(timer);
                };
            };
            wrapper.addEventListener('click', handleButtonClick);
            return () => wrapper.removeEventListener('click', handleButtonClick);
        });

        function removeProductFromPmCart(RecordId: number | string): void {
            if (typeof RecordId === 'number') {
                setPmCartMessage({ status: 'empty', message: props.resources.productSelectionSpaceText });
                delete pmProducts[RecordId];
                cookiePMset();
            } else {
                typeof window !== 'undefined' && localStorage.setItem('pm_products', JSON.stringify({}));
                setPmCartMessage({ status: 'empty', message: props.resources.productSelectionSpaceText });
                setPmProducts({});
            }
        }

        React.useEffect(() => {
            // @ts-ignore
            setTotalItems(Object.values(pmProducts).reduce((t, { Qty }) => Number(t) + Number(Qty), 0));
            // @ts-ignore
            const totalNewProducts = Object.values(pmProducts).map(tp => tp.Qty * tp.Price);
            setTotalPrice(totalNewProducts.reduce((tp, qp) => tp + qp, 0));
        }, [pmProducts]);

        const _addToCart = async () => {
            const currentCartState = await getCartState(props.context?.actionContext);
            const pickAndMixItems = Object.values(pmProducts);
            const productsInfo: {
                name: string;
                id: string;
                price: string;
                quantity: string;
                // dimension12: string;
                list: string;
                category: string;
            }[] = [];

            if (pickAndMixItems.length >= 1) {
                pickAndMixItems.forEach(data => {
                    // @ts-ignore
                    const dataPrice = parseFloat(data.Price).toFixed(2);
                    productsInfo.push({
                        //@ts-ignore
                        name: data.Item,
                        //@ts-ignore
                        id: data.Description,
                        price: dataPrice,
                        //@ts-ignore
                        quantity: data.Qty,
                        //@ts-ignore
                        // 'dimension12': data.Id,
                        list: 'Pick&Mix',
                        category: 'Pick&Mix'
                    });
                    //@ts-ignore
                    localStorage.setItem(data.Id ?? '', 'Pick&Mix' + ';' + 'Pick&Mix');
                });
            }

            trackAddToCartFromListing(
                props.context,
                {
                    RecordId: 0,
                    ItemId: '199000',
                    Name: 'My Venchi Mix',
                    BasePrice: totalPrice,
                    Price: totalPrice,
                    AdjustedPrice: totalPrice,
                    ProductTypeValue: 0
                },
                categoryUserPathString,
                listName
            );

            trackAddVenchiMix(
                productsInfo.map(p => ({
                    category: p.name,
                    action: Number(p.quantity),
                    label: Number(p.price)
                }))
            );

            pickAndMixItems.forEach(v => {
                // @ts-ignore
                delete v.PrimaryImageUrl;
            });
            //finally
            addPickAndMixListAsInfocodes_CAPAsync(
                { callerContext: props.context?.actionContext },
                JSON.stringify(pickAndMixItems),
                currentCartState.cart
            ).then(async res => {
                await currentCartState.refreshCart({});
                const cartLinesToPatch: CartLine[] | undefined = currentCartState.cart.CartLines?.filter(
                    c =>
                        c.ItemId === '199000' &&
                        !c.ExtensionProperties?.filter(e => e.Key === 'venchiProductCategoryHierarchy')[0] &&
                        !c.ExtensionProperties?.filter(e => e.Key === 'venchiProductListName')[0]
                );
                const cartLinesUpdated: CartLine[] = cartLinesToPatch
                    ? cartLinesToPatch?.map(c => {
                          let commerceProperties: CommerceProperty[] = c.ExtensionProperties ? c.ExtensionProperties : [];
                          commerceProperties = [];
                          commerceProperties.push(
                              {
                                  Key: 'venchiProductListName',
                                  Value: { StringValue: listName }
                              },
                              {
                                  Key: 'venchiProductCategoryHierarchy',
                                  Value: { StringValue: categoryUserPathString }
                              }
                          );
                          return {
                              ...c,
                              ExtensionProperties: commerceProperties
                          };
                      })
                    : [];
                if (cartLinesUpdated.length) {
                    await currentCartState.updateCartLines({
                        updatedCartLinesObject: cartLinesUpdated
                    });
                }
            });

            removeProductFromPmCart('reset');
            setPmCartMessage({ status: 'success', message: props.resources.pmSuccessAddedToCartMessage });
            setTimeout(() => {
                setPmCartMessage({ status: 'empty', message: props.resources.productSelectionSpaceText });
            }, 4000);
        };
        // @ts-ignore
        const AddToCartButtons = ({ total }) => {
            if (total === 0) {
                return (
                    <>
                        <button disabled className={'solid-button add-to-cart'}>
                            {props.resources.addToCartButtonText}
                        </button>
                        {/* tslint:disable-next-line:jsx-no-lambda react-this-binding-issue */}

                        <div className={`${pmCartMessage.status} empty_cart__text`}>{pmCartMessage.message}</div>
                    </>
                );
            }

            return (
                <>
                    <button onClick={_addToCart} className={'solid-button add-to-cart'}>
                        {props.resources.addToCartButtonText}
                    </button>
                    {/* tslint:disable-next-line:jsx-no-lambda react-this-binding-issue */}
                    <button className={'hyperlink reset'} onClick={() => removeProductFromPmCart('reset')}>
                        {props.resources.removeAllButtonText}
                    </button>
                </>
            );
        };

        function mobileTriggerPmCart(): void {
            // tslint:disable-next-line:no-shadowed-variable
            setOpenPmCart(openPmCart => !openPmCart);
            openPmCart === true ? document.body.classList.remove('no-scroll-pick-mix') : document.body.classList.add('no-scroll-pick-mix');
        }
        return (
            <div className={'row pick-mix'}>
                <div className={'col-lg-7'}>
                    <div className={'category-intro__ctn'} />
                    <Node {...ProductsContainer}>{products}</Node>
                </div>
                <div className={'pm-cart--outer col-lg-4 offset-lg-1'}>
                    <div className={'pm-cart--ctn'}>
                        <h3>{props.resources.pickAndMixCartTitle}</h3>
                        <Node className={'pm-cart'}>
                            <>
                                <div className={'pm-cart--top'} data-show_pm_cart={openPmCart}>
                                    <div className={'pm-cart--total'}>
                                        <div className={'pm-cart--total-items'}>
                                            <span className={'icon-pm-cart icon'} />
                                            <span className={'text'}>{totalItems}</span>
                                        </div>
                                        <div className={'pm-cart--total-price'}>
                                            {props?.context.cultureFormatter.formatCurrency(totalPrice)}
                                        </div>
                                        <button
                                            data-show_pm_cart={openPmCart}
                                            className={'mobile_open-cart'}
                                            onClick={mobileTriggerPmCart}
                                        />
                                    </div>
                                    <AddToCartButtons total={totalItems} />
                                </div>
                                <div className={'pm-cart--product-list'} data-show_pm_cart={openPmCart}>
                                    {Object.keys(pmProducts).map((keyName, i) => {
                                        return (
                                            <div className='pm-product' key={i}>
                                                <div className={'image'}>
                                                    <img
                                                        alt={pmProducts[keyName].Item}
                                                        src={`${pmProducts[keyName].PrimaryImageUrl}&w=64&h=64&q=80&m=6&f=jpg`}
                                                    />
                                                </div>
                                                <div className={'product-info'}>
                                                    <div className={'name'}>{pmProducts[keyName]?.Item}</div>
                                                    <div className={'quantity'}>Quantity: {pmProducts[keyName]?.Qty}</div>
                                                    <div className={'price'}>
                                                        {props?.context.cultureFormatter.formatCurrency(pmProducts[keyName]?.Price)}
                                                    </div>
                                                    <div className={'msc-product__extra-data'}>
                                                        <span className={'data-product-id'}>{pmProducts[keyName]?.Id}</span>
                                                        <span className={'data-product-sku'}>{pmProducts[keyName]?.Description}</span>
                                                    </div>
                                                </div>
                                                {/* tslint:disable-next-line:jsx-no-lambda */}
                                                <button
                                                    className={'remove-from-pmcart'}
                                                    onClick={() => removeProductFromPmCart(Number(keyName))}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        </Node>
                    </div>
                </div>
            </div>
        );
    }
    // if (isMobile) {
    //     return (
    //         <Module {...SearchResultContainer}>
    //             {renderCategoryHierarchy(categoryHierarchy)}
    //             {renderTitle(TitleViewProps)}
    //             {choiceSummary}
    //             {modalToggle}
    //             {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
    //             <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
    //             <Node {...ProductsContainer}>
    //                 {errorMessage}
    //                 {products}
    //             </Node>
    //             {pagination}
    //         </Module>
    //     );
    // }
    // return (
    //     <Module {...SearchResultContainer}>
    //         <Node {...CategoryNavContainer}>
    //             {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
    //             {TitleViewProps && renderTitleCount(TitleViewProps)}
    //         </Node>
    //         <Node {...RefineAndProductSectionContainer}>
    //             {refineMenu && renderRefiner(refineMenu)}
    //             <Node {...ProductSectionContainer}>
    //                 {TitleViewProps && renderTitle(TitleViewProps)}
    //                 {choiceSummary}
    //                 {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
    //                 <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
    //                 <Node {...ProductsContainer}>
    //                     {errorMessage}
    //                     {products}
    //                 </Node>
    //                 {pagination}
    //             </Node>
    //         </Node>
    //     </Module>
    // );

    const [scrollY, setScrollY] = React.useState(0);

    React.useEffect(() => {
        let search_qParam = 'fallback_empty_search';
        if (props.context.request.query && props.context.request.query.q) {
            search_qParam = props.context.request.query.q;
            // Saving the user's expectations -- What were they actually searching for?
        }

        // @ts-ignore
        if (products && products.props.products.length === 0) {
            if (window.dataLayer) {
                window.dataLayer?.push?.({
                    event: 'gaEvent',
                    eventCategory: 'site_search',
                    eventAction: 'unexpected_behaviour',
                    eventLabel: search_qParam
                });
            }
        }
    }, []); // Empty dependency list = this only gets executed once

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        let ticking = false;

        const onScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    setScrollY(window.scrollY || 0);
                    ticking = false;
                });
                ticking = true;
            }
        };

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <>
            <div onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className={`back-to-top ${scrollY > 200 ? '' : 'hidden'}`}>
                <i className='fa-arrow-up' />
            </div>

            {TitleViewProps && !hideTitle && renderTitle(TitleViewProps, displayTitle || false, isSearchPage || false, categoryH1Title)}

            <Row fluid className='ms-search-result__sticky-top-bar'>
                <Container className='mobile-refiners-row'>
                    <div className='mobile-refiners-row__button'>
                        {modalToggle}
                        {refinerStatusCount ? <div className='mobile-refiners-row__refiners-count'>{refinerStatusCount}</div> : null}
                    </div>
                    <div className='mobile-refiners-row__count'>{TitleViewProps.title.titleCount}</div>
                </Container>
            </Row>
            <Module {...SearchResultContainer}>
                {createSearchResultModal(searchResultModal, refineMenu, choiceSummary, sortByOptions, isRecoSearchPage)}
                <Node {...RefineAndProductSectionContainer}>
                    <div className='desktop-refiners-left-container'>{refineMenu && renderRefiner(refineMenu)}</div>
                    <Node {...ProductSectionContainer}>
                        <Row fluid className='desktop_products_top_bar'>
                            <div className='ms-search-result__collection-title-count'>{TitleViewProps?.title?.titleCount}</div>
                            <div className='ms-search-result-wrapper-sort-by-category'>
                                {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                            </div>
                        </Row>
                        <div className='desktop_choice-summary'>{choiceSummary}</div>
                        <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                        <Node {...ProductsContainer}>
                            {errorMessage}
                            {_getProducts()}
                            {pagination}
                        </Node>
                    </Node>
                </Node>
            </Module>
        </>
    );
};

const SearchResultModalWrapper: React.FC<{
    modalProps: ISearchResultModalViewProps;
    refineMenu: IRefineMenuViewProps;
    choiceSummary_element: React.ReactNode;
    sortByDropDown: ISortByViewProps;
    isRecoSearchPage?: string;
}> = ({ modalProps, refineMenu, choiceSummary_element, sortByDropDown, isRecoSearchPage }) => {
    // Espandi i submenu quando il modal si apre su mobile
    React.useEffect(() => {
        const expandSubmenusOnMobile = () => {
            // Controlla se siamo su mobile
            const isMobile = window.innerWidth <= 992; // $msv-breakpoint-l

            if (isMobile) {
                // Aspetta un po' per permettere al DOM di renderizzare
                setTimeout(() => {
                    const collapsedToggles = document.querySelectorAll('.ms-refine-submenu__toggle_collapsed');
                    collapsedToggles.forEach(toggle => {
                        if (toggle instanceof HTMLElement) {
                            toggle.click();
                        }
                    });
                }, 100);
            }
        };

        expandSubmenusOnMobile();
    });

    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, choiceSummary_element, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    choiceSummary_element: React.ReactNode,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return (
        <SearchResultModalWrapper
            modalProps={modalProps}
            refineMenu={refineMenu}
            choiceSummary_element={choiceSummary_element}
            sortByDropDown={sortByDropDown}
            isRecoSearchPage={isRecoSearchPage}
        />
    );
};

const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    choiceSummary_element: React.ReactNode,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(
            props.modalBody,
            {},
            renderSort(sortByDropDown, isRecoSearchPage),
            choiceSummary_element,
            renderRefiner(refineMenu)
        );
    }
    return null;
};

const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

//IMPORTED PRODUCT COMPONENT USAGE TO ADD THE DATALAYER PROPS

interface ISearchResultContainerResourcesExtended extends ISearchResultContainerResources {
    addToCartText: string;
    omnibusRecentLowestPrice: string;
    outOfStockString: string;
    omnibus_recent_price_label: string;
}

interface IProductSearchResultItemsExtended {
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: ISearchResultContainerResourcesExtended;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    productCategoryUserPath?: string;
    productListName?: string;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

const ProductSearchResultItems: React.FC<IProductSearchResultItemsExtended> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    productCategoryUserPath,
    productListName
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };
    return (
        <ul className='list-unstyled'>
            {products.map((product: ProductSearchResult, index: number) => (
                <li className='ms-product-search-result__item' key={index}>
                    <ProductComponent
                        context={context}
                        telemetryContent={telemetryContent}
                        imageSettings={imageSettings}
                        freePriceText={resources.priceFree}
                        originalPriceText={resources.originalPriceText}
                        currentPriceText={resources.currentPriceText}
                        ratingAriaLabel={resources.ratingAriaLabel}
                        allowBack={allowBackNavigation}
                        id={moduleId}
                        key={product.RecordId}
                        typeName={moduleType}
                        data={{ product }}
                        quickViewButton={quickviewSlot}
                        productComparisonButton={productComparisonButton}
                        inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                        isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                        priceResources={priceResources}
                        swatchItemAriaLabel={resources.swatchItemAriaLabel}
                        dimensionAvailabilities={productsDimensionAvailabilities?.find(
                            dimensionAvailability =>
                                ArrayExtensions.hasElements(dimensionAvailability) &&
                                dimensionAvailability[0].masterProductId === product.MasterProductId
                        )}
                        productCategoryUserPath={productCategoryUserPath}
                        productListName={productListName}
                        resources={resources}
                    />
                </li>
            ))}
        </ul>
    );
};

const renderTitle = (props: ITitleViewProps, displayTitle: boolean, renderPrefix: boolean, titleOvverride?: string): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title && displayTitle) {
        return (
            <Node {...TitleContainer}>
                <h1>
                    {renderPrefix ? title.titlePrefix : null}
                    {titleOvverride ? titleOvverride : title.titleText}
                </h1>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
